/**
 * AddColumnHeader — Sticky "+" column header for adding columns to the table.
 *
 * Features:
 * - Plus icon button that opens a searchable dropdown
 * - Two-step drill-in: options with `children` (e.g. an association and its
 *   lookup properties) render as a group row that drills into a second panel
 *   listing its children, with a back row. This keeps the top level compact
 *   instead of dumping every association's every property inline.
 * - Search behavior depends on drill-in state: at the top level, search
 *   flattens across every level (top-level options AND group children) so
 *   any column is findable by typing. Once drilled into a group, search is
 *   scoped to that group's children only.
 * - Optional "Create new property" action at bottom
 */

import {
  ArrowLeft,
  ChevronRight,
  Plus,
  SearchLg,
  Settings01,
} from '@untitledui/icons';
import type { FC } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react-aria-components';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger,
  Input as AriaInput,
  ListBox,
  ListBoxItem,
  SearchField,
} from 'react-aria-components';

import { NonModalAriaPopover } from '@/lib/base/overlays/non-modal-aria-popover';
import { cx } from '@/utils/cx';

import { styles } from '../styles';

export interface AddColumnOption {
  /** Column/property identifier. For a group row, this is a navigation-only id
   *  (never passed to `onAddColumn`). */
  id: string;
  /** Display label */
  label: string;
  /** Optional icon for the property type */
  icon?: FC<{ className?: string }>;
  /** When present, this option is an expandable GROUP: selecting it drills into
   *  a second panel listing these children instead of adding a column. Leaf
   *  options (no children) add a column when selected. */
  children?: AddColumnOption[];
  /** Label to show when this option surfaces in flat search results. Defaults
   *  to "<group label> → <child label>" for group children. Lets a group's
   *  primary child (e.g. "add the association itself") read cleanly in search. */
  searchLabel?: string;
}

export interface AddColumnHeaderProps {
  /** Available columns that can be added (not currently visible) */
  availableColumns: AddColumnOption[];
  /** Callback when a column is selected to add */
  onAddColumn: (columnId: string) => void;
  /** Callback for "Create new property" action */
  onCreateAttribute?: () => void;
  /** Size variant — matches table size for consistent height */
  size?: keyof typeof styles.sizes;
}

/** Sentinel id for the drill-in back row (never collides with a column id). */
const BACK_ROW_ID = '__add_column_back__';

type DisplayRow =
  | {
      kind: 'leaf';
      id: string;
      label: string;
      icon?: FC<{ className?: string }>;
    }
  | {
      kind: 'group';
      id: string;
      label: string;
      icon?: FC<{ className?: string }>;
    }
  | { kind: 'back'; id: string; label: string };

/**
 * Sticky "+" header cell for adding columns to the table view.
 */
export function AddColumnHeader({
  availableColumns,
  onAddColumn,
  onCreateAttribute,
  size = 'md',
}: AddColumnHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // The group currently drilled into (null = top-level panel). A query scopes
  // results to the active group's children when drilled in; at the top level a
  // query still shows the flat, cross-level result list.
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  // Scrollable option list, plus the top-level scroll offset to restore when the
  // user navigates back out of a group. Drilling in resets to the top of the new
  // panel; going back returns to exactly where they were.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topLevelScrollTopRef = useRef(0);

  useLayoutEffect(() => {
    // Entering a group starts at the top of the drill-in panel; returning to the
    // top level restores the offset the user drilled in from.
    const target = activeGroupId ? 0 : topLevelScrollTopRef.current;
    const apply = () => {
      const el = scrollContainerRef.current;
      if (el) {
        el.scrollTop = target;
      }
    };
    // Apply immediately (no flash on drill-in), then again after layout settles:
    // the panel we're leaving may be shorter than the one we're entering, so an
    // immediate restore can clamp to the old (smaller) scroll height. The rAF
    // re-applies once the taller content is laid out.
    apply();
    const raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [activeGroupId]);

  const rows = useMemo<DisplayRow[]>(() => {
    const query = searchQuery.trim().toLowerCase();

    // Resolve the active group once — reused by both the search and drill-in
    // branches below, so "the group vanished" (availableColumns changed under
    // us) is handled in exactly one place: `activeGroup` comes back undefined
    // and both branches fall through to the top-level behavior.
    const activeGroup =
      activeGroupId != null
        ? availableColumns.find(
            option => option.id === activeGroupId && option.children
          )
        : undefined;

    if (query) {
      // Drilled into a group: scope search to that group's children only,
      // using each child's plain label (not the "group → child" search label
      // used by the flat top-level results below).
      if (activeGroup?.children) {
        return activeGroup.children
          .map(
            (child): DisplayRow => ({
              kind: 'leaf',
              id: child.id,
              label: child.label,
              icon: child.icon,
            })
          )
          .filter(row => row.label.toLowerCase().includes(query));
      }

      // Top level (or the active group vanished): flatten every leaf
      // (top-level + group children) so any column is findable by typing.
      const flat: DisplayRow[] = [];
      for (const option of availableColumns) {
        if (option.children) {
          for (const child of option.children) {
            flat.push({
              kind: 'leaf',
              id: child.id,
              label: child.searchLabel ?? `${option.label} → ${child.label}`,
              icon: child.icon,
            });
          }
        } else {
          flat.push({
            kind: 'leaf',
            id: option.id,
            label: option.label,
            icon: option.icon,
          });
        }
      }
      return flat.filter(row => row.label.toLowerCase().includes(query));
    }

    // Drill-in panel: back row + the active group's children.
    if (activeGroup?.children) {
      return [
        { kind: 'back', id: BACK_ROW_ID, label: activeGroup.label },
        ...activeGroup.children.map(
          (child): DisplayRow => ({
            kind: 'leaf',
            id: child.id,
            label: child.label,
            icon: child.icon,
          })
        ),
      ];
    }

    // Top-level panel: leaves add directly, groups drill in.
    return availableColumns.map(
      (option): DisplayRow =>
        option.children
          ? {
              kind: 'group',
              id: option.id,
              label: option.label,
              icon: option.icon,
            }
          : {
              kind: 'leaf',
              id: option.id,
              label: option.label,
              icon: option.icon,
            }
    );
  }, [availableColumns, searchQuery, activeGroupId]);

  const resetTransientState = useCallback(() => {
    setSearchQuery('');
    setActiveGroupId(null);
    topLevelScrollTopRef.current = 0;
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        resetTransientState();
      }
    },
    [resetTransientState]
  );

  const handleAction = useCallback(
    (key: Key) => {
      const keyStr = String(key);

      if (keyStr === BACK_ROW_ID) {
        setActiveGroupId(null);
        return;
      }

      // A group row on the top-level panel drills in rather than adding a column.
      // (In search mode there are no group rows — only flattened leaves.)
      // Treat `activeGroupId` as inactive when it no longer exists as a group in
      // `availableColumns`: if the open group vanished (schema/view options
      // changed under us), `rows` falls back to the top-level list, so a click
      // here is a top-level group row that must drill in — not add a bogus
      // `group:<id>` column.
      const activeGroupStillExists =
        activeGroupId != null &&
        availableColumns.some(
          option => option.id === activeGroupId && option.children
        );
      if (!searchQuery.trim() && !activeGroupStillExists) {
        const option = availableColumns.find(
          candidate => candidate.id === keyStr
        );
        if (option?.children) {
          // Remember where we were so "back" can restore this scroll offset.
          topLevelScrollTopRef.current =
            scrollContainerRef.current?.scrollTop ?? 0;
          setActiveGroupId(keyStr);
          return;
        }
      }

      onAddColumn(keyStr);
      setIsOpen(false);
      resetTransientState();
    },
    [
      availableColumns,
      searchQuery,
      activeGroupId,
      onAddColumn,
      resetTransientState,
    ]
  );

  const handleCreateAttribute = useCallback(() => {
    onCreateAttribute?.();
    setIsOpen(false);
    resetTransientState();
  }, [onCreateAttribute, resetTransientState]);

  // Don't render if no columns available and no create action
  if (availableColumns.length === 0 && !onCreateAttribute) {
    return null;
  }

  return (
    <div
      role='columnheader'
      className={cx(
        styles.common.headerCell,
        styles.sizes[size].headerCell,
        'flex items-center justify-center w-12 min-w-12 shrink-0'
      )}
    >
      <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
        <AriaButton
          aria-label='Add column'
          className={cx(
            'p-1.5 rounded-md cursor-pointer transition-colors duration-100',
            'text-fg-quaternary hover:text-fg-secondary hover:bg-[var(--color-bg-secondary)]',
            'outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2'
          )}
        >
          <Plus className='size-4' />
        </AriaButton>
        <NonModalAriaPopover
          placement='bottom end'
          className={cx(
            'w-64 max-h-80 overflow-hidden rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt',
            'origin-(--trigger-anchor-point) will-change-transform',
            'entering:duration-150 entering:ease-out entering:animate-in entering:fade-in entering:slide-in-from-top-0.5',
            'exiting:duration-100 exiting:ease-in exiting:animate-out exiting:fade-out exiting:slide-out-to-top-0.5'
          )}
        >
          {/* The Dialog takes the id DialogTrigger stamps into the trigger's
              aria-controls and owns focus + Escape — the non-modal popover
              root is not a dialog, so without it both would be lost. */}
          <AriaDialog
            aria-label='Add column'
            className='flex flex-col outline-hidden'
          >
            {/* Search field */}
            <div className='p-2 border-b border-[var(--color-border-secondary)]'>
              <SearchField
                value={searchQuery}
                onChange={setSearchQuery}
                aria-label='Search columns'
                className='w-full'
              >
                <div className='relative'>
                  <SearchLg className='absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-fg-quaternary pointer-events-none' />
                  <AriaInput
                    autoFocus
                    placeholder='Search columns'
                    className={cx(
                      'w-full pl-8 pr-3 py-1.5 text-sm rounded-md',
                      'bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]',
                      'placeholder:text-[var(--color-text-tertiary)]',
                      'border border-[var(--color-border-secondary)]',
                      'outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-transparent'
                    )}
                  />
                </div>
              </SearchField>
            </div>

            {/* Column list */}
            <div
              ref={scrollContainerRef}
              className='overflow-y-auto max-h-52 py-1'
            >
              {rows.length === 0 ? (
                <div className='px-3 py-2 text-sm text-[var(--color-text-tertiary)]'>
                  {searchQuery
                    ? 'No columns match your search'
                    : 'All columns are visible'}
                </div>
              ) : (
                <ListBox
                  aria-label='Available columns'
                  selectionMode='none'
                  onAction={handleAction}
                  className='outline-none'
                >
                  {rows.map(row => (
                    <ListBoxItem
                      key={row.id}
                      id={row.id}
                      textValue={row.label}
                      className={cx('px-1.5 py-px outline-none cursor-pointer')}
                    >
                      {({ isFocused, isHovered }) => (
                        <div
                          className={cx(
                            'flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors duration-100',
                            // Hover AND focus, not focus alone. react-aria
                            // treats them as separate states, and a bare
                            // ListBox (unlike a Select's popover listbox) does
                            // not move focus on hover — so keying the
                            // highlight off `isFocused` only left mousing over
                            // an option with no feedback at all.
                            (isFocused || isHovered) && 'bg-primary_hover'
                          )}
                        >
                          {row.kind === 'back' ? (
                            <>
                              <ArrowLeft className='size-4 text-fg-quaternary shrink-0' />
                              <span className='text-sm font-medium text-[var(--color-text-secondary)] truncate'>
                                {row.label}
                              </span>
                            </>
                          ) : (
                            <>
                              {row.icon && (
                                <row.icon className='size-4 text-fg-quaternary shrink-0' />
                              )}
                              <span className='flex-1 text-sm font-medium text-[var(--color-text-secondary)] truncate'>
                                {row.label}
                              </span>
                              {row.kind === 'group' && (
                                <ChevronRight className='size-4 text-fg-quaternary shrink-0' />
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </ListBoxItem>
                  ))}
                </ListBox>
              )}
            </div>

            {/* Create new property action */}
            {onCreateAttribute && (
              <div className='border-t border-[var(--color-border-secondary)] p-1.5'>
                <button
                  type='button'
                  onClick={handleCreateAttribute}
                  className={cx(
                    'w-full flex items-center gap-2 px-2.5 py-2 rounded-md',
                    'text-sm font-medium text-[var(--color-text-secondary)]',
                    'hover:bg-primary_hover transition-colors duration-100 cursor-pointer'
                  )}
                >
                  <Settings01 className='size-4 text-fg-quaternary' />
                  Create new property
                </button>
              </div>
            )}
          </AriaDialog>
        </NonModalAriaPopover>
      </DialogTrigger>
    </div>
  );
}
